import { localStorageVariables } from '@/types/localStorageVariables';
import { applayoutService, MenuItems } from '@/components/layout/app-layout-menuServive';
import { PROGRESS_STEP_SEQUENCE } from '@/contexts/ProgressContext';
import { menuVisibilityService } from '@/lib/services/MenuVisibilityService';

/**
 * Enhanced MenuWorkflowMapper that uses dynamic menu items from the API
 * - Loads menu structure from API instead of hardcoded mapping
 * - Handles menu state based on API response
 * - Maintains workflow progression based on completed steps
 * - Handles special non-menu steps with chained mappings
 */
export class MenuWorkflowMapper {
    private static menuService = new applayoutService();
    private static menuItems: MenuItems[] = [];
    private static menuItemsLoaded = false;


    /**
     * Initialize menu items from API
     */
    public static async initialize(): Promise<void> {
        try {
            console.log('🗺️ MenuWorkflowMapper: Initializing with API menu items');
            this.menuItems = await this.menuService.getMenuItems();
            this.menuItemsLoaded = true;
            this.updateMenuWorkflowStep();
            console.log('🗺️ MenuWorkflowMapper: Initialization complete', {
                menuItemsCount: this.menuItems.length
            });
        } catch (error) {
            console.error('Failed to initialize menu items:', error);
            this.menuItems = [];
            this.menuItemsLoaded = false;
        }
    }

    /**
     * Get the next menu step based on the current completed step
     * Returns the next step in the sequence after the completed step
     * Skips steps that are not visible (e.g., people section when hidden)
     */
    private static getNextStep(currentStep: string): string {
        // Filter out hidden menu items based on visibility service
        const visibleMenuItems = this.menuItems.filter(item => 
            menuVisibilityService.shouldShowMenuItem(item.SystemMenuName)
        );
        
        // Cast to string array for proper type handling
        const steps = visibleMenuItems.map(item => item.SystemMenuName.toLowerCase());
        
        if (!currentStep) {
            // No step completed yet, return first step
            const firstStep = steps.length > 0 ? steps[0] : 'getstarted';
            console.log('🗺️ MenuWorkflowMapper: No completed step, returning first step', { firstStep });
            return firstStep;
        }

        // Find the current step in the sequence
        const currentStepLower = currentStep.toLowerCase();
        const stepIndex = steps.findIndex(
            step => step.toLowerCase() === currentStepLower
        );

        // If step not found, it might be a hidden step - find next visible step after it
        if (stepIndex === -1) {
            // Check if the current step exists in the full menu but is hidden
            const fullSteps = this.menuItems.map(item => item.SystemMenuName.toLowerCase());
            const fullStepIndex = fullSteps.findIndex(
                step => step.toLowerCase() === currentStepLower
            );
            
            if (fullStepIndex !== -1) {
                // Current step exists but is hidden, find next visible step after it
                for (let i = fullStepIndex + 1; i < this.menuItems.length; i++) {
                    const nextMenuItem = this.menuItems[i];
                    if (menuVisibilityService.shouldShowMenuItem(nextMenuItem.SystemMenuName)) {
                        console.log('🗺️ MenuWorkflowMapper: Skipped hidden step, next visible step', {
                            skippedStep: currentStep,
                            nextStep: nextMenuItem.SystemMenuName.toLowerCase()
                        });
                        return nextMenuItem.SystemMenuName.toLowerCase();
                    }
                }
            }
            
            console.log('🗺️ MenuWorkflowMapper: Step not found in visible steps', { 
                currentStep, 
                stepIndex 
            });
            return currentStep;
        }

        // If is the last visible step, return the current step
        if (stepIndex >= steps.length - 1) {
            console.log('🗺️ MenuWorkflowMapper: Is last visible step', { 
                currentStep, 
                stepIndex 
            });
            return currentStep;
        }

        // Return the next visible step in sequence
        const nextStep = steps[stepIndex + 1];
        console.log('🗺️ MenuWorkflowMapper: Resolved next step', {
            currentStep,
            nextStep,
            stepIndex,
            visibleStepsCount: steps.length
        });

        return nextStep;
    }

    /**
     * Get all completed steps up to the current step
     */
    public static getCompletedSteps(): string[] {
        const lastCompletedStep = localStorage.getItem(localStorageVariables.LastCompletedStepPagenName);
        if (!lastCompletedStep) return [];

        // Ensure lastCompletedStep is of the correct type for indexOf
        const stepIndex = PROGRESS_STEP_SEQUENCE.findIndex(
            step => step === lastCompletedStep
        );
        if (stepIndex === -1) return [];

        return PROGRESS_STEP_SEQUENCE.slice(0, stepIndex + 1);
    }

    /**
     * Get the current step (next uncompleted step)
     */
    public static getCurrentStep(): string {
        const lastCompletedStep = localStorage.getItem(localStorageVariables.LastCompletedStepPagenName);
        
        // Cast to string[] to help TypeScript understand this can have elements
        const steps = PROGRESS_STEP_SEQUENCE as unknown as string[];
        
        // Defensive: If no steps are defined, return empty string
        if (!steps.length) return "";

        // If no step is completed, return first step
        if (!lastCompletedStep) {
            return steps[0];
        }

        const stepIndex = steps.findIndex(step => step === lastCompletedStep);
        
        // If step not found in sequence, return first step
        if (stepIndex === -1) {
            return steps[0];
        }

        // Get next step if available
        return stepIndex + 1 < steps.length 
            ? steps[stepIndex + 1]
            : lastCompletedStep;
    }

    /**
     * Refresh menu items from cache (call after menu service clears its cache)
     */
    public static async refreshMenuItems(): Promise<void> {
        try {
            this.menuService.clearCache();
            this.menuItems = await this.menuService.getMenuItems();
            this.menuItemsLoaded = true;
            console.log('🗺️ MenuWorkflowMapper: Refreshed menu items, count:', this.menuItems.length);
        } catch (error) {
            console.error('Failed to refresh menu items:', error);
        }
    }

    /**
     * Enhanced update logic using dynamic menu items
     * READS LastCompletedStepPagenName from localStorage (set by API)
     * WRITES MenuWorkflowStep to localStorage for menu display
     */
    public static updateMenuWorkflowStep(applicantStatus?: string): void {
        const lastCompletedPage = localStorage.getItem(localStorageVariables.LastCompletedStepPagenName) || '';
        let menuWorkflowStep = '';

        // Get next step following the mapping chain
        menuWorkflowStep = this.getNextStep(lastCompletedPage);
        
        console.log('🗺️ MenuWorkflowMapper: updateMenuWorkflowStep', {
            lastCompletedPage,
            menuWorkflowStep,
            menuItemsCount: this.menuItems.length
        });

        // Only update if we have a valid step and it's different from current
        const currentMenuStep = localStorage.getItem(localStorageVariables.MenuWorkflowStep);
        if (menuWorkflowStep && currentMenuStep !== menuWorkflowStep) {
            localStorage.setItem(localStorageVariables.MenuWorkflowStep, menuWorkflowStep);

            // Dispatch event to notify menu components
            window.dispatchEvent(new CustomEvent('menuWorkflowStepChanged', {
                detail: {
                    menuWorkflowStep,
                    lastCompletedPage,
                    completedSteps: this.getCompletedSteps(),
                    currentStep: this.getCurrentStep(),
                    applicantStatus,
                    source: 'MenuWorkflowMapper-Dynamic'
                }
            }));
        }
    }

    /**
     * Gets the current menu workflow step
     */
    public static getCurrentMenuWorkflowStep(): string {
        return localStorage.getItem(localStorageVariables.MenuWorkflowStep) || 'getstarted';
    }

    /**
     * Initialize MenuWorkflowStep from existing localStorage after login/refresh
     */
    public static async initializeAfterLogin(): Promise<void> {
        // Ensure menu items are loaded
        if (!this.menuItemsLoaded) {
            await this.initialize();
        }

        const lastCompletedStep = localStorage.getItem(localStorageVariables.LastCompletedStepPagenName);

        if (lastCompletedStep && lastCompletedStep.trim() !== '') {
            this.updateMenuWorkflowStep();
        } else {
            localStorage.setItem(localStorageVariables.MenuWorkflowStep, 'getstarted');

            window.dispatchEvent(new CustomEvent('menuWorkflowStepChanged', {
                detail: {
                    menuWorkflowStep: 'getstarted',
                    lastCompletedPage: '',
                    completedSteps: [],
                    currentStep: 'getstarted',
                    source: 'MenuWorkflowMapper-initializeAfterLogin'
                }
            }));
        }
    }

    /**
     * Debug function to verify menu step logic
     */
    public static debugCurrentState(): void {
        const lastCompletedStep = localStorage.getItem(localStorageVariables.LastCompletedStepPagenName) || '';
        const menuWorkflowStep = localStorage.getItem(localStorageVariables.MenuWorkflowStep) || '';
        const completedSteps = this.getCompletedSteps();
        const currentStep = this.getCurrentStep();

        console.log('🔍 MenuWorkflowMapper Debug:', {
            lastCompletedStepFromAPI: lastCompletedStep,
            currentMenuMilestone: menuWorkflowStep,
            completedSteps,
            currentStep,
            menuItemsLoaded: this.menuItemsLoaded,
            menuItemsCount: this.menuItems.length,
            nextStep: this.getNextStep(lastCompletedStep),
            explanation: `API says "${lastCompletedStep}" was completed, so menu milestone "${menuWorkflowStep}" should be current`
        });
    }
} 